import { Config } from 'config/envConfig';
import React, { forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faComments,
    faDatabase,
    faRobot,
    faChartLine,
    faShieldAlt,
    faGlobe,
    faCode,
    faBrain,
} from '@fortawesome/free-solid-svg-icons';

const Functionalities = forwardRef((props, ref) => {
    const useSecuredConnection = Config.USE_SECURED_CONNECTION;

    const mainFeatures = [
        {
            icon: faComments,
            title: 'Chat with Your Data',
            subtitle: 'Making data your teammate',
            description:
                'Engage in dynamic discussions while seamlessly integrating multiple PDF, DOCX, TXT, and MD files directly into the conversation. Support for databases like MySQL, PostgreSQL, and MS SQL. Experience fluid communication even with complex documents comprising text, images, and graphics.',
            highlights: [
                'PDFs, DOCX, TXT & MD',
                'SQLite Files',
                'MS SQL / PostgreSQL',
                'OpenAI GPT & Google Gemini',
            ],
            image: '/Function1.png',
            color: 'blue',
        },
        {
            icon: faDatabase,
            title: 'Data Extraction',
            subtitle: 'Save 100s of hours',
            description:
                'Easily extract structured information from multiple files, significantly reducing the time and effort traditionally spent on manual processes. Automatically organize and extract structured information to reclaim valuable time and allocate resources more efficiently.',
            highlights: [
                'Batch Processing',
                'Structured Output',
                'Multiple File Types',
                'Automated Extraction',
            ],
            image: '/Function2.png',
            color: 'emerald',
        },
        {
            icon: faRobot,
            title: 'Automation Through Agents',
            subtitle: 'Build custom AI workflows',
            description:
                'Build and deploy custom AI agents that automate workflows in plain English. Agents can send emails, call APIs, or update databases. The system can auto-trigger agents based on user queries in natural language.',
            highlights: [
                'Custom AI Agents',
                'Email Automation',
                'API Integration',
                'Database Updates',
            ],
            image: '/Function3.png',
            color: 'purple',
        },
        {
            icon: faChartLine,
            title: 'Reporting & Dashboard',
            subtitle: 'Actionable insights',
            description:
                'Transform extracted data into actionable insights by publishing as Excel or professionally formatted PDF reports. Create visually stunning dashboards from databases without any knowledge of SQL.',
            highlights: [
                'Excel Export',
                'PDF Reports',
                'Visual Dashboards',
                'No SQL Required',
            ],
            image: '/Function4.png',
            color: 'orange',
        },
        {
            icon: faShieldAlt,
            title: 'Access Control',
            subtitle: 'Enterprise-grade security',
            description:
                'Fine-grained access control configurable via natural language rules. Manage information access to specific users as per enterprise needs. Role-based permissions and comprehensive audit logging for compliance.',
            highlights: [
                'Natural Language Rules',
                'User-level Access',
                'Role-based Permissions',
                'Audit Logging',
            ],
            image: '/Function5.png',
            color: 'red',
        },
        {
            icon: faGlobe,
            title: 'Multi-Channel Access',
            subtitle: 'Access anywhere',
            description:
                'Access Quyva via web, email, Telegram, and Microsoft Teams. Conversational bot capabilities for support or sales queries. Embeddable JavaScript widget for seamless integration into your website or application.',
            highlights: [
                'Web Access',
                'Email Integration',
                'Telegram Bot',
                'Embeddable Widget',
            ],
            image: '/Function6.png',
            color: 'cyan',
        },
    ];

    const getColorClasses = (color) => {
        const colors = {
            blue: {
                bg: 'bg-landing-blue-500/10',
                text: 'text-landing-blue-500',
                border: 'border-landing-blue-500',
            },
            emerald: {
                bg: 'bg-landing-emerald-500/10',
                text: 'text-landing-emerald-500',
                border: 'border-landing-emerald-500',
            },
            purple: {
                bg: 'bg-landing-purple-500/10',
                text: 'text-landing-purple-500',
                border: 'border-landing-purple-500',
            },
            orange: {
                bg: 'bg-landing-orange-500/10',
                text: 'text-landing-orange-500',
                border: 'border-landing-orange-500',
            },
            red: {
                bg: 'bg-landing-red-500/10',
                text: 'text-landing-red-500',
                border: 'border-landing-red-500',
            },
            cyan: {
                bg: 'bg-landing-cyan-500/10',
                text: 'text-landing-cyan-500',
                border: 'border-landing-cyan-500',
            },
        };
        return colors[color] || colors.blue;
    };

    return (
        <div ref={ref}>
            {/* Features Header */}
            <section className='py-24 bg-landing-white'>
                <div className='max-w-7xl mx-auto px-6'>
                    <div className='text-center mb-16'>
                        <span className='text-landing-blue-500 font-bold tracking-wide uppercase text-sm'>
                            Features
                        </span>
                        <h2 className='text-3xl md:text-4xl font-bold text-landing-slate-900 mt-2 mb-6'>
                            Powerful Capabilities for Your Enterprise
                        </h2>
                        <p className='text-xl text-landing-slate-600 max-w-3xl mx-auto'>
                            Quyva combines advanced AI with intuitive interfaces
                            to transform how you interact with your data
                        </p>
                    </div>

                    {/* Feature Cards Grid */}
                    <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16'>
                        {mainFeatures.map((feature, index) => {
                            const colorClasses = getColorClasses(feature.color);
                            return (
                                <div
                                    key={index}
                                    className='bg-landing-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow border border-landing-gray-100'
                                >
                                    <div
                                        className={`w-14 h-14 ${colorClasses.bg} rounded-xl flex items-center justify-center mb-6`}
                                    >
                                        <FontAwesomeIcon
                                            icon={feature.icon}
                                            className={`${colorClasses.text} text-2xl`}
                                        />
                                    </div>
                                    <h3 className='text-xl font-bold text-landing-slate-900 mb-2'>
                                        {feature.title}
                                    </h3>
                                    <p
                                        className={`${colorClasses.text} text-sm font-medium mb-4`}
                                    >
                                        {feature.subtitle}
                                    </p>
                                    <p className='text-landing-slate-600 mb-6 text-sm leading-relaxed'>
                                        {feature.description}
                                    </p>
                                    <div className='flex flex-wrap gap-2'>
                                        {feature.highlights.map(
                                            (highlight, hIndex) => (
                                                <span
                                                    key={hIndex}
                                                    className='text-xs bg-landing-white border border-landing-gray-200 rounded-full px-3 py-1 text-landing-slate-600'
                                                >
                                                    {highlight}
                                                </span>
                                            )
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Innovation Quote Section */}
            <section className='py-24 bg-gradient-to-br from-landing-slate-900 to-landing-slate-800 relative overflow-hidden'>
                <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                <div className='max-w-7xl mx-auto px-6 relative z-10'>
                    <div className='max-w-4xl mx-auto text-center'>
                        <h2 className='text-3xl md:text-4xl font-bold text-landing-white mb-6'>
                            Transform How Your Enterprise Works with Data
                        </h2>
                        <p className='text-xl text-landing-slate-300 mb-8'>
                            Quyva brings the power of advanced AI to your
                            fingertips, enabling natural language interactions
                            with all your enterprise data sources.
                        </p>
                        <div className='flex flex-wrap justify-center gap-6'>
                            <div className='flex items-center gap-3 bg-landing-blue-500/10 rounded-full px-5 py-3 border border-landing-blue-500/20'>
                                <FontAwesomeIcon
                                    icon={faBrain}
                                    className='text-landing-blue-400'
                                />
                                <span className='text-landing-white'>
                                    OpenAI GPT
                                </span>
                            </div>
                            <div className='flex items-center gap-3 bg-landing-blue-500/10 rounded-full px-5 py-3 border border-landing-blue-500/20'>
                                <FontAwesomeIcon
                                    icon={faBrain}
                                    className='text-landing-emerald-400'
                                />
                                <span className='text-landing-white'>
                                    Google Gemini
                                </span>
                            </div>
                            <div className='flex items-center gap-3 bg-landing-blue-500/10 rounded-full px-5 py-3 border border-landing-blue-500/20'>
                                <FontAwesomeIcon
                                    icon={faCode}
                                    className='text-landing-purple-400'
                                />
                                <span className='text-landing-white'>
                                    Custom LLMs
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
});

export default Functionalities;
